import { useState, useMemo, useEffect } from 'react';
import { useApiData } from '../../../../contexts/apiService';
import type { RFPLeaseStatItem } from '../../../../contexts/apiService';
import { useDataManager } from '../../../../contexts/dataManager';
import Dropdown, { type DropdownOption } from '../../../../components/common/dropdown/dropdown';
import type { LeaseRow } from '../rfpTypes';

interface CustomerDataImportProps {
    onLeasesParsed: (rows: LeaseRow[]) => void;
}

interface ProducerOption {
    producer_id: number;
    producer_name: string;
}

export default function CustomerDataImport({ onLeasesParsed }: CustomerDataImportProps) {
    const { apiData, fetchRFPLeaseStats } = useApiData();
    const { hasTableData, requestTableData } = useDataManager();

    const [selectedProducerId, setSelectedProducerId] = useState<number | null>(null);
    const [leaseStats, setLeaseStats] = useState<RFPLeaseStatItem[]>([]);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [imported, setImported] = useState(false);

    useEffect(() => {
        if (!hasTableData('producer_definition')) requestTableData('producer_definition');
    }, []);

    // Customers from producer_definition (the producer/customer master registry), active only
    const producerOptions: DropdownOption[] = useMemo(() => {
        const seen = new Map<number, ProducerOption>();
        for (const item of apiData?.producer_definition ?? []) {
            const isActive = (item as any).is_active === 1 || (item as any).is_active === true;
            if (item.id && isActive && !seen.has(item.id)) {
                seen.set(item.id, { producer_id: item.id, producer_name: item.producer_name });
            }
        }
        return Array.from(seen.values())
            .sort((a, b) => a.producer_name.localeCompare(b.producer_name))
            .map(p => ({ value: String(p.producer_id), label: p.producer_name }));
    }, [apiData?.producer_definition]);

    const handleProducerChange = async (producerIdStr: string) => {
        const producerId = Number(producerIdStr);
        setSelectedProducerId(producerId);
        setLeaseStats([]);
        setSelectedIds(new Set());
        setImported(false);
        setError(null);
        setLoading(true);
        try {
            const stats = await fetchRFPLeaseStats(producerId);
            setLeaseStats(stats);
            setSelectedIds(new Set(stats.map(s => s.lease_coord_id)));
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to load lease data.');
        } finally {
            setLoading(false);
        }
    };

    const toggleLease = (id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleAll = () => {
        if (selectedIds.size === leaseStats.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(leaseStats.map(s => s.lease_coord_id)));
        }
    };

    const handleImport = () => {
        const rows: LeaseRow[] = leaseStats
            .filter(s => selectedIds.has(s.lease_coord_id))
            .map(s => ({
                lease_name:                 s.lease_name,
                avg_monthly_production_bbl: s.avg_monthly_bbl,
                avg_bsw:                    s.avg_bsw,
                avg_gravity:                s.avg_gravity,
                latitude:                   s.latitude,
                longitude:                  s.longitude,
                region:                     s.region ?? undefined,
            }));
        onLeasesParsed(rows);
        setImported(true);
    };

    const allSelected = leaseStats.length > 0 && selectedIds.size === leaseStats.length;

    return (
        <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                Import from existing customer data
            </div>
            <Dropdown
                options={producerOptions}
                value={selectedProducerId !== null ? String(selectedProducerId) : ''}
                onChange={handleProducerChange}
                placeholder="Select customer…"
                width={240}
            />

            {loading && (
                <p style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>Loading lease data…</p>
            )}
            {error && (
                <p style={{ fontSize: 12, color: '#dc2626', marginTop: 8 }}>{error}</p>
            )}

            {!loading && leaseStats.length > 0 && (
                <div style={{ marginTop: 10, border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '6px 10px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb',
                    }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600, color: '#374151' }}>
                            <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                            {selectedIds.size} / {leaseStats.length} leases selected
                        </label>
                        <button
                            onClick={handleImport}
                            disabled={selectedIds.size === 0}
                            style={{
                                fontSize: 12, padding: '4px 14px',
                                background: selectedIds.size > 0 ? '#2563eb' : '#9ca3af',
                                color: '#fff', border: 'none', borderRadius: 5,
                                cursor: selectedIds.size > 0 ? 'pointer' : 'not-allowed',
                                fontWeight: 600,
                            }}
                        >
                            {imported ? '✓ Imported' : 'Import'}
                        </button>
                    </div>
                    <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                            <thead>
                                <tr style={{ background: '#f3f4f6', textAlign: 'left' }}>
                                    <th style={{ padding: '4px 8px', width: 28 }} />
                                    <th style={{ padding: '4px 8px' }}>Lease</th>
                                    <th style={{ padding: '4px 8px', textAlign: 'right' }}>BBL/mo</th>
                                    <th style={{ padding: '4px 8px', textAlign: 'right' }}>BS&W %</th>
                                    <th style={{ padding: '4px 8px', textAlign: 'right' }}>Gravity</th>
                                    <th style={{ padding: '4px 8px' }}>Region</th>
                                </tr>
                            </thead>
                            <tbody>
                                {leaseStats.map(s => {
                                    const checked = selectedIds.has(s.lease_coord_id);
                                    return (
                                        <tr
                                            key={s.lease_coord_id}
                                            onClick={() => toggleLease(s.lease_coord_id)}
                                            style={{
                                                cursor: 'pointer',
                                                background: checked ? '#eff6ff' : undefined,
                                                borderTop: '1px solid #f3f4f6',
                                            }}
                                        >
                                            <td style={{ padding: '4px 8px' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => toggleLease(s.lease_coord_id)}
                                                    onClick={e => e.stopPropagation()}
                                                />
                                            </td>
                                            <td style={{ padding: '4px 8px', color: '#1f2937', fontWeight: 500 }}>{s.lease_name}</td>
                                            <td style={{ padding: '4px 8px', textAlign: 'right', color: '#374151' }}>
                                                {s.avg_monthly_bbl.toLocaleString()}
                                            </td>
                                            <td style={{ padding: '4px 8px', textAlign: 'right', color: s.avg_bsw === 0 ? '#9ca3af' : '#374151' }}>
                                                {s.avg_bsw.toFixed(2)}
                                            </td>
                                            <td style={{ padding: '4px 8px', textAlign: 'right', color: s.avg_gravity === 0 ? '#9ca3af' : '#374151' }}>
                                                {s.avg_gravity.toFixed(1)}
                                            </td>
                                            <td style={{ padding: '4px 8px', color: '#6b7280' }}>{s.region ?? '—'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {!loading && selectedProducerId !== null && leaseStats.length === 0 && !error && (
                <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>No active leases found for this customer.</p>
            )}
        </div>
    );
}
